import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import { getSubscriptionLogs } from '../../../Services/User/SubscriptionPlans';

import InnerBanner from '../../../Components/InnerBanner/InnerBanner';
import './SubscriptionLogs.css';

const SubscriptionLogs = () => {
  usePageTitle('Subscription Logs');
  const navigate = useNavigate();

  const { data: response, isLoading } = useQuery({
    queryKey: ['subscription-logs'],
    queryFn: getSubscriptionLogs,
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const renderCurrentSubscription = (subscription) => {
    return (
      <div className="subscription-card current-subscription mb-4">
        <h2 className="section-title">Current Subscription</h2>
        <h3 className="subscription-card-title border-0">{subscription.subscription_name}</h3>
        <div className="subscription-details">
          <div className="subscription-detail-row border-0">
            <span className="detail-label">Subscription Type:</span>
            <span className="detail-value">{subscription.subscription_type}</span>
          </div>
          <div className="subscription-detail-row border-0">
            <span className="detail-label">Subscribed On:</span>
            <span className="detail-value">{formatDate(subscription.subscribed_on)}</span>
          </div>
          <div className="subscription-detail-row border-0">
            <span className="detail-label">Expires On:</span>
            <span className="detail-value">{formatDate(subscription.expires_on)}</span>
          </div>
          <div className="subscription-detail-row border-0">
            <span className="detail-label">Price Paid:</span>
            <span className="detail-value">${subscription.price_paid}</span>
          </div>
          {subscription.discount && (
            <div className="subscription-detail-row border-0">
              <span className="detail-label">Discount:</span>
              <span className="detail-value discount-value">{subscription.discount} 🔥</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPastSubscription = (subscription, index) => {
    return (
      <Col md={6} key={index} className="mb-4">
        <div className="subscription-card past-subscription">
          <h4 className="subscription-card-title">{subscription.subscription_name}</h4>
          <div className="subscription-details">
            <div className="subscription-detail-row">
              <span className="detail-label">Subscription Type:</span>
              <span className="detail-value">{subscription.subscription_type}</span>
            </div>
            <div className="subscription-detail-row">
              <span className="detail-label">Subscribed On:</span>
              <span className="detail-value">{formatDate(subscription.subscribed_on)}</span>
            </div>
            <div className="subscription-detail-row">
              <span className="detail-label">Price Paid:</span>
              <span className="detail-value">${subscription.price_paid}</span>
            </div>
            {subscription.discount && (
              <div className="subscription-detail-row">
                <span className="detail-label">Discount:</span>
                <span className="detail-value discount-value">{subscription.discount} 🔥</span>
              </div>
            )}
          </div>
        </div>
      </Col>
    );
  };

  if (isLoading) {
    return (
      <>
        <InnerBanner heading="Subscription Logs" />
        <section className="py-sm-5 py-4">
          <Container>
            <Row>
              <Col xs={12} className="text-center">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3">Loading subscription logs...</p>
              </Col>
            </Row>
          </Container>
        </section>
      </>
    );
  }

  if (!response?.current_subscription && (!response?.past_subscriptions || response.past_subscriptions.length === 0)) {
    return (
      <>
        <InnerBanner heading="Subscription Logs" />
        <section className="py-sm-5 py-4">
          <Container>
            <Row>
              <Col xs={12} className="text-center">
                <p className="mt-3">No subscription logs available</p>
              </Col>
            </Row>
          </Container>
        </section>
      </>
    );
  }

  return (
    <>
      <InnerBanner heading="Subscription Logs" />
      <section className="py-sm-5 py-4">
        <Container>
          <Row>
            <Col xs={12}>
              {response?.current_subscription && (
                <div className="subscription-section mb-5">
                  {renderCurrentSubscription(response.current_subscription)}
                </div>
              )}

              {response?.past_subscriptions && response.past_subscriptions.length > 0 && (
                <div className="subscription-section">
                  <h2 className="section-title">Past Subscription</h2>
                  <Row>
                    {response.past_subscriptions.map((subscription, index) =>
                      renderPastSubscription(subscription, index)
                    )}
                  </Row>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default SubscriptionLogs;
